import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import CartItem from "../components/CartItem";
import api from "../api/client";
import { toast } from "sonner";
import { ShoppingBag, Tag } from "lucide-react";

export default function Cart() {
    const { items, subtotal, loading } = useCart();
    const { user } = useAuth();
    const nav = useNavigate();
    const [coupon, setCoupon] = useState("");
    const [appliedCoupon, setAppliedCoupon] = useState(null);

    const discount = appliedCoupon
        ? appliedCoupon.type === "percent"
            ? Math.min(subtotal, subtotal * (appliedCoupon.value / 100))
            : Math.min(subtotal, appliedCoupon.value)
        : 0;
    const taxable = Math.max(subtotal - discount, 0);
    const shipping = taxable >= 499 ? 0 : 49;
    const tax = Math.round(taxable * 0.05 * 100) / 100;
    const total = taxable + tax + shipping;

    const applyCoupon = async () => {
        try {
            const { data } = await api.post("/coupons/validate", { code: coupon });
            setAppliedCoupon(data);
            toast.success(`Coupon "${data.code}" applied!`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Invalid coupon");
        }
    };

    const checkout = () => {
        if (!user) {
            toast.info("Please sign in to checkout");
            nav("/login?next=/checkout");
            return;
        }
        nav("/checkout", { state: { couponCode: appliedCoupon?.code } });
    };

    if (loading) {
        return <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 text-center text-brand-moss">Loading cart...</div>;
    }

    if (!items.length) {
        return (
            <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 animate-fade-slide">
                <div data-testid="empty-cart" className="bg-white border border-brand-line rounded-3xl p-12 text-center">
                    <ShoppingBag size={48} className="mx-auto text-brand-moss mb-4" />
                    <h2 className="font-heading font-black text-3xl md:text-4xl mb-3">Your cart is empty</h2>
                    <p className="text-brand-moss mb-8">Time to fill it with crunchy goodness.</p>
                    <Link
                        to="/products"
                        data-testid="cart-continue-shopping"
                        className="inline-block bg-brand-orange text-white font-bold px-8 py-4 rounded-xl btn-lift"
                    >
                        Shop now
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 animate-fade-slide">
            <h1 className="font-heading font-black text-4xl md:text-6xl tracking-tighter mb-10">Your cart</h1>
            <div className="grid lg:grid-cols-[1fr,380px] gap-10">
                <div className="space-y-4">
                    {items.map((it) => <CartItem key={it.product_id} item={it} />)}
                </div>
                <aside className="space-y-4 lg:sticky lg:top-28 self-start">
                    <div className="bg-white border border-brand-line rounded-2xl p-6">
                        <h3 className="font-heading font-bold text-xl mb-4">Coupon</h3>
                        <div className="flex gap-2">
                            <input
                                value={coupon}
                                onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                                placeholder="Enter code"
                                data-testid="coupon-input"
                                className="flex-1 bg-brand-cream border-none rounded-xl px-4 py-3 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-brand-orange"
                            />
                            <button
                                onClick={applyCoupon}
                                data-testid="coupon-apply"
                                className="bg-brand-ink text-white px-5 rounded-xl font-bold text-sm hover:bg-brand-orange transition"
                            >
                                Apply
                            </button>
                        </div>
                        {appliedCoupon && (
                            <div className="mt-3 inline-flex items-center gap-2 bg-brand-sage/10 text-brand-sage px-3 py-2 rounded-lg text-xs font-bold">
                                <Tag size={12} /> {appliedCoupon.code} applied · {appliedCoupon.description}
                            </div>
                        )}
                        <p className="text-xs text-brand-moss mt-3">Try: WELCOME10, CRUNCH50, HEALTHY20</p>
                    </div>

                    <div className="bg-white border border-brand-line rounded-2xl p-6">
                        <h3 className="font-heading font-bold text-xl mb-4">Summary</h3>
                        <div className="space-y-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-brand-moss">Subtotal</span>
                                <span className="font-bold" data-testid="subtotal">₹{subtotal.toFixed(2)}</span>
                            </div>
                            {discount > 0 && (
                                <div className="flex justify-between text-brand-sage">
                                    <span>Discount</span>
                                    <span className="font-bold">-₹{discount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-brand-moss">Tax (5% GST)</span>
                                <span className="font-bold">₹{tax.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-brand-moss">Shipping</span>
                                <span className="font-bold">{shipping === 0 ? "FREE" : `₹${shipping}`}</span>
                            </div>
                            {shipping > 0 && (
                                <p className="text-xs text-brand-moss">Add ₹{(499 - taxable).toFixed(0)} more for free shipping</p>
                            )}
                            <div className="border-t border-brand-line pt-3 flex justify-between items-center">
                                <span className="font-heading font-bold text-lg">Total</span>
                                <span className="font-heading font-black text-2xl" data-testid="cart-total">
                                    ₹{total.toFixed(2)}
                                </span>
                            </div>
                        </div>
                        <button
                            onClick={checkout}
                            data-testid="checkout-btn"
                            className="w-full mt-5 bg-brand-orange text-white font-bold py-4 rounded-xl btn-lift shadow-[0_4px_14px_0_rgba(255,107,53,0.39)]"
                        >
                            Proceed to Checkout
                        </button>
                        <Link to="/products" className="block text-center mt-3 text-sm font-bold text-brand-moss hover:text-brand-orange">
                            ← Continue shopping
                        </Link>
                    </div>
                </aside>
            </div>
        </div>
    );
}
